import { styled } from '@mui/material/styles';
import { Box, Typography, Grid, Button } from '@mui/material';
import styledSC, { css } from 'styled-components';

// Custom props that should not be forwarded to DOM
const customProps = ['isToday', 'isSelected', 'hasAvailability', 'isPast', 'isCurrentMonth', 'isDiscounted', 'isBestPrice'];
const shouldForwardProp = (prop) => !customProps.includes(prop);

export const CalendarContainer = styled(Box)(({ theme }) => ({
  //padding: theme.spacing(2),
  backgroundColor: '#fff',
  borderRadius: theme.shape.borderRadius,
  //boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  marginBottom: theme.spacing(3),
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    margin: 0,
    borderRadius: 0,
    boxShadow: 'none',
  }
}));

export const CalendarHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
  }
}));

export const WeekDaysGrid = styled(Grid)(() => ({
  width: '100%',
  margin: 0,
  '& > .MuiGrid-item': {
    padding: 0,
    width: `${100 / 7}%`,
    flexShrink: 0,
  }
}));

export const WeekDayHeader = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontWeight: 500,
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5),
    fontSize: '0.8rem',
  }
}));

export const WeekRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  '&:last-child': {
    borderBottom: 'none'
  }
}));

export const StyledCalendarBox = styled(Box, { shouldForwardProp })(({ theme, isCurrentMonth, isSelected, hasAvailability, isPast }) => ({
  width: `${100 / 7}%`,
  flexShrink: 0,
  height: '80px',
  padding: theme.spacing(1),
  backgroundColor: isPast
    ? theme.palette.grey[100]
    : isSelected
      ? `${theme.palette.primary.main}15` // Selected background takes precedence
      : isCurrentMonth ? '#fff' : theme.palette.grey[50],
  borderRight: `1px solid ${theme.palette.grey[200]}`,
  cursor: hasAvailability && !isPast ? 'pointer' : 'not-allowed',
  opacity: isPast ? 0.6 : 1,
  position: 'relative',
  transition: 'all 0.2s ease',
  boxSizing: 'border-box',
  ...(isSelected && {
    borderColor: theme.palette.primary.main,
    boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
    '& .date-number': {
      color: theme.palette.primary.main,
      fontWeight: 600
    },
    '& .price-tag': {
      fontWeight: 600
    }
  }),
  // Hover state
  ...(hasAvailability && !isPast && {
    '&:hover': {
      backgroundColor: isSelected
        ? `${theme.palette.primary.main}15` // Keep selected background on hover
        : `${theme.palette.primary.main}08`,
      transform: 'translateY(-1px)',
      boxShadow: isSelected
        ? `inset 0 0 0 1px ${theme.palette.primary.main}, 0 2px 4px rgba(0,0,0,0.1)` // Combine selected border with hover shadow
        : '0 2px 4px rgba(0,0,0,0.1)',
      '& .price-tag': {
        transform: 'translateY(-1px)',
      }
    }
  }),
  '&:last-child': {
    borderRight: 'none'
  },
  [theme.breakpoints.down('sm')]: {
    height: '70px',
    padding: theme.spacing(0.5),
  }
}));

// Step 2: Create a wrapper component that filters out the custom props
export const CalendarCell = ({ 
  isPast, 
  isCurrentMonth, 
  isSelected, 
  hasAvailability, 
  children, 
  ...otherProps 
}) => {
  return (
    <StyledCalendarBox
      isPast={isPast}
      isCurrentMonth={isCurrentMonth}
      isSelected={isSelected}
      hasAvailability={hasAvailability}
      {...otherProps}
    >
      {children}
    </StyledCalendarBox>
  );
};

export const DateNumber = styled(Typography, { shouldForwardProp })(({ theme, isCurrentMonth, isToday }) => ({
  fontSize: '0.9rem',
  color: isCurrentMonth ? theme.palette.text.primary : theme.palette.text.disabled,
  transition: 'all 0.2s ease',
  className: 'date-number',
  ...(isToday && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '-2px'
  })
}));

export const InstantBookingIcon = styled(Box)(() => ({
  position: 'absolute',
  top: 4,
  right: 4,
  zIndex: 2,
  '& svg': {
    filter: 'drop-shadow(0px 1px 1px rgba(0,0,0,0.1))', // Subtle shadow for better visibility
  }
}));

export const CommunityIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 4,
  left: 4,
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.warning.main,
  boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
  zIndex: 2,
  cursor: 'pointer',
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'scale(1.3)',
  }
}));

export const BookingButtonContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(2, 0),
  marginTop: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 0),
  }
}));

export const BookingButton = styled(Button)(({ theme }) => ({
  minWidth: '200px',
  padding: theme.spacing(1.5),
  fontSize: '0.95rem',
  [theme.breakpoints.down('sm')]: {
    width: '90%',
    fontSize: '0.9rem',
    whiteSpace: 'normal',
    height: 'auto',
    padding: theme.spacing(1),
  }
}));



export const PriceTag = styled(Box)(() => ({
  position: 'absolute',
  bottom: 8,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  textAlign: 'center',
  zIndex: 1,
  transition: 'color 0.2s ease',
}));

export const OriginalPrice = styled(Typography)(({ theme }) => ({
  fontSize: '0.7rem', // Slightly smaller for better hierarchy
  textDecoration: 'line-through',
  color: theme.palette.text.secondary,
  opacity: 0.7,
  marginBottom: '2px',
}));

export const CurrentPrice = styled(Typography)(({ theme, isLowest, isLower }) => ({
  fontSize: '1.1rem', // Slightly larger
  fontWeight: 500,
  color: isLowest 
    ? theme.palette.primary.main
    : isLower 
      ? theme.palette.green.sad
      : theme.palette.text.primary,
  transition: 'color 0.2s ease',
  className: 'price-tag'
}));


export const PriceBubble = styled(Box)(({ theme, isLowest }) => ({
  position: 'absolute',
  top: -8,
  right: -8,
  backgroundColor: isLowest ? theme.palette.primary.main : theme.palette.success.main,
  color: '#fff',
  borderRadius: '50%',
  width: '16px',
  height: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.7rem',
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  '&::after': {
    content: isLowest ? '"L"' : '"S"',
  }
}));

export const AvailabilityDot = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 4,
  left: 4,
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
  opacity: 0.8,
}));

export const BookingSummaryContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(2),
  padding: 0,
  boxSizing: 'border-box',

  '& .MuiPaper-root': {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  },

  '& .booking-status': {
    textAlign: 'center',
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },

  [theme.breakpoints.down('sm')]: {
    '& .MuiButton-root': {
      padding: theme.spacing(1, 2),
    }
  }
}));


// Optional: if you want to style the price separately
export const BookingPrice = styled(Box)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  
  '& .currency': {
    fontSize: '0.8em',
    position: 'relative',
    top: '-0.1em',
  }
}));

// Enhanced CalendarDay with modern visual states (styled-components version)
// Can be used as an alternative to MUI-based StyledCalendarBox

export const CalendarDayStyled = styledSC.div`
  width: ${100 / 7}%;
  min-height: 80px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-right: 1px solid #E5E5E5;
  position: relative;
  transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
  background: #fff;
  cursor: pointer;
  
  &:last-child {
    border-right: none;
  }
  
  /* Disabled state - Past dates */
  ${props => props.isDisabled && css`
    background: #FAFAFA;
    cursor: not-allowed;
    opacity: 0.5;
    
    &:hover {
      background: #FAFAFA;
    }
  `}
  
  /* Available state */
  ${props => props.isAvailable && !props.isDisabled && css`
    background: #fff;
    
    &:hover {
      background: rgba(255, 159, 28, 0.05);
      transform: translateY(-2px);
      box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
    }
  `}
  
  /* Selected state */
  ${props => props.isSelected && css`
    background: rgba(255, 159, 28, 0.1);
    border-color: #FF9F1C;
    box-shadow: inset 0 0 0 2px #FF9F1C;
    
    &:hover {
      background: rgba(255, 159, 28, 0.15);
    }
  `}
  
  /* Best price indicator */
  ${props => props.isBestPrice && css`
    &::after {
      content: 'Best';
      position: absolute;
      top: 4px;
      right: 4px;
      font-size: 10px;
      font-weight: 600;
      color: #22C55E;
      background: #DCFCE7;
      padding: 2px 6px;
      border-radius: 4px;
    }
  `}
  
  /* Instant booking indicator */
  ${props => props.isInstantBooking && css`
    &::before {
      content: '⚡';
      position: absolute;
      top: 4px;
      left: 4px;
      font-size: 12px;
    }
  `}
  
  @media (max-width: 768px) {
    min-height: 60px;
    padding: 4px;
  }
`;

export const CalendarDayNumber = styledSC.span`
  font-size: 14px;
  font-weight: 500;
  color: #171717;
  margin-bottom: 4px;
  
  ${props => props.isToday && css`
    background: #FF9F1C;
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  `}
  
  ${props => props.isDisabled && css`
    color: #A3A3A3;
  `}
`;

export const CalendarDayPrice = styledSC.span`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.isBestPrice ? '#22C55E' : '#171717'};
  
  ${props => props.hasDiscount && css`
    &::before {
      content: '${props => props.originalPrice}';
      text-decoration: line-through;
      color: #A3A3A3;
      font-weight: 400;
      font-size: 11px;
      display: block;
    }
  `}
`;

// Booking Summary Sidebar with modern styling
export const SummaryContainer = styledSC.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
  position: sticky;
  top: 24px;
  
  ${props => props.glass && css`
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.3);
  `}
`;

export const SummaryTitle = styledSC.h3`
  font-size: 18px;
  font-weight: 600;
  color: #171717;
  margin: 0 0 20px 0;
  padding-bottom: 16px;
  border-bottom: 2px solid #F5F5F5;
`;

export const SummaryItem = styledSC.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  font-size: 15px;
  
  .label {
    color: #737373;
    font-weight: 400;
  }
  
  .value {
    color: #171717;
    font-weight: 500;
  }
`;

export const SummaryTotal = styledSC.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  margin-top: 16px;
  border-top: 2px solid #F5F5F5;
  font-size: 18px;
  font-weight: 700;
  
  .total-label {
    color: #171717;
  }
  
  .total-value {
    color: #FF9F1C;
    font-size: 24px;
  }
`;

export const ConfirmBookingButton = styledSC.button`
  width: 100%;
  background: #FF9F1C;
  color: white;
  padding: 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 16px;
  border: none;
  cursor: pointer;
  margin-top: 20px;
  transition: all 200ms ease;
  
  &:hover:not(:disabled) {
    background: #E68A00;
    transform: translateY(-2px);
    box-shadow: 0 8px 12px -3px rgba(255, 159, 28, 0.3);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;